import React from 'react';
import './EntityDetailsModal.css';

const EntityDetailsModal = ({ entity, onClose }) => {
  if (!entity) return null;

  const getRiskColor = (risk) => {
    switch(risk) {
      case 'high_risk': return '#f44336';
      case 'suspicious': return '#ff9800';
      case 'clean': return '#4caf50';
      default: return '#999';
    }
  };

  const getTypeIcon = (type) => {
    // Check if it's a tainted address category
    if (type === 'ransomware') return '🔒';
    if (type === 'darknet_market') return '🕵️';
    if (type === 'known_hack' || type === 'hack') return '🚨';
    if (type === 'scam') return '⚠️';
    if (type === 'mixer') return '🌪️';
    if (type === 'sanctioned') return '⛔';
    
    // Regular entity types
    switch(type) {
      case 'exchange': return '�';
      case 'mining_pool': return '⛏️';
      case 'darknet': return '�️';
      default: return '🏷️';
    }
  };

  const getRiskEmoji = (risk) => {
    switch(risk) {
      case 'high_risk': return '🚨';
      case 'suspicious': return '⚠️';
      case 'clean': return '✅';
      default: return '❓';
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // Optional: Show a toast notification
  };

  return (
    <div className="entity-modal-overlay" onClick={onClose}>
      <div className="entity-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="entity-modal-close" onClick={onClose}>×</button>
        
        <div className="entity-modal-header">
          <div className="entity-modal-icon">
            {entity.category_icon || getTypeIcon(entity.entity_type)}
          </div>
          <h2>{entity.label}</h2>
          {entity.is_tainted && (
            <div className="tainted-warning-badge">
              {entity.verified ? '🚨 VERIFIED THREAT' : '⚠️ AUTO-DETECTED'}
            </div>
          )}
        </div>

        <div className="entity-modal-body">
          {/* TAINTED ADDRESS: Show severity and direction */}
          {entity.is_tainted && (
            <>
              <div className="entity-detail-row highlight-row">
                <div className="entity-detail-label">⚠️ Warning</div>
                <div className="entity-detail-value">
                  <strong style={{ color: '#dc3545' }}>
                    This address is flagged for fraudulent activity!
                  </strong>
                </div>
              </div>

              {entity.severity !== undefined && (
                <div className="entity-detail-row">
                  <div className="entity-detail-label">Severity</div>
                  <div className="entity-detail-value">
                    <span className={`severity-badge severity-${entity.severity}`}>
                      {entity.severity}/10
                    </span>
                  </div>
                </div>
              )}

              {entity.direction && (
                <div className="entity-detail-row">
                  <div className="entity-detail-label">Transaction Flow</div>
                  <div className="entity-detail-value">
                    {entity.direction === 'input' ? '⬅️ Input (received from)' : '➡️ Output (sent to)'}
                  </div>
                </div>
              )}

              {entity.value_btc !== undefined && (
                <div className="entity-detail-row">
                  <div className="entity-detail-label">Amount</div>
                  <div className="entity-detail-value">
                    <strong>{entity.value_btc.toFixed(8)} BTC</strong>
                  </div>
                </div>
              )}

              {entity.verified !== undefined && (
                <div className="entity-detail-row">
                  <div className="entity-detail-label">Verification Status</div>
                  <div className="entity-detail-value">
                    {entity.verified ? (
                      <span style={{ color: '#dc3545' }}>✅ Verified by {entity.source}</span>
                    ) : (
                      <span style={{ color: '#fd7e14' }}>⚠️ Auto-detected (unverified)</span>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Address */}
          <div className="entity-detail-row">
            <div className="entity-detail-label">Address</div>
            <div className="entity-detail-value address-value">
              <code>{entity.address}</code>
              <button 
                className="copy-btn" 
                onClick={() => copyToClipboard(entity.address)}
                title="Copy to clipboard"
              >
                📋
              </button>
            </div>
          </div>

          {/* Type */}
          <div className="entity-detail-row">
            <div className="entity-detail-label">Type</div>
            <div className="entity-detail-value">
              <span className={`entity-type-badge type-${entity.entity_type}`}>
                {getTypeIcon(entity.entity_type)} {entity.entity_type.replace('_', ' ')}
              </span>
            </div>
          </div>

          {/* Risk Level */}
          <div className="entity-detail-row">
            <div className="entity-detail-label">Risk Level</div>
            <div className="entity-detail-value">
              <span 
                className="risk-badge" 
                style={{ backgroundColor: getRiskColor(entity.risk_level) }}
              >
                {getRiskEmoji(entity.risk_level)} {entity.risk_level.replace('_', ' ')}
              </span>
            </div>
          </div>

          {/* Source */}
          <div className="entity-detail-row">
            <div className="entity-detail-label">Source</div>
            <div className="entity-detail-value">
              {entity.source || 'Manual'}
            </div>
          </div>

          {/* Depth (if available) */}
          {entity.depth !== undefined && (
            <div className="entity-detail-row">
              <div className="entity-detail-label">Depth in Path</div>
              <div className="entity-detail-value">
                {entity.depth}
              </div>
            </div>
          )}

          {/* Description (if available) */}
          {entity.description && (
            <div className="entity-detail-row">
              <div className="entity-detail-label">Description</div>
              <div className="entity-detail-value">
                {entity.description}
              </div>
            </div>
          )}

          {/* Tags (for tainted addresses) */}
          {entity.tags && entity.tags.length > 0 && (
            <div className="entity-detail-row">
              <div className="entity-detail-label">Tags</div>
              <div className="entity-detail-value">
                <div className="entity-tags">
                  {entity.tags.map((tag, idx) => (
                    <span key={idx} className="entity-tag">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="entity-modal-footer">
          <button className="entity-modal-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default EntityDetailsModal;
